import { useEffect, useState } from 'react';
import { Users, UserCheck, Clock, BarChart3 } from 'lucide-react';

interface ActiveAttendance {
  id: string;
  user_id: string;
  clock_in: string;
  status: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

interface WaitingUser {
  id: string;
  name: string;
  phone: string;
  email: string;
}

interface ChatStats {
  total: number;
  active: number;
  closed: number;
  redistributed: number;
}

export default function PainelAtendimento() {
  const [activeAttendance, setActiveAttendance] = useState<ActiveAttendance[]>([]);
  const [waitingChats, setWaitingChats] = useState<WaitingUser[]>([]);
  const [stats, setStats] = useState<ChatStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showAssignModal, setShowAssignModal] = useState<string | null>(null);
  const [assigningChat, setAssigningChat] = useState<string | null>(null);
  const [allAttendants, setAllAttendants] = useState<any[]>([]);

  useEffect(() => {
    loadCurrentUser();
    loadData();
    // Atualizar a cada 15 segundos
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin;

      // Carregar atendentes com chats ativos (sellers com usuários atribuídos)
      try {
        const resCustomers = await fetch(`${baseUrl}/api/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const dataCustomers = await resCustomers.json();
        
        if (dataCustomers.success && dataCustomers.data) {
          // Agrupar por seller_id para contar chats ativos
          const sellerCounts: { [key: string]: { user: any, count: number, lastInteraction: string } } = {};
          
          dataCustomers.data.forEach((customer: any) => {
            if (customer.seller_id && customer.seller) {
              if (!sellerCounts[customer.seller_id]) {
                sellerCounts[customer.seller_id] = {
                  user: customer.seller,
                  count: 0,
                  lastInteraction: customer.last_activity || customer.created_at
                };
              }
              sellerCounts[customer.seller_id].count++;
              // Manter a última interação mais recente
              if (customer.last_activity && customer.last_activity > sellerCounts[customer.seller_id].lastInteraction) {
                sellerCounts[customer.seller_id].lastInteraction = customer.last_activity;
              }
            }
          });
          
          // Converter para formato de ActiveAttendance
          const activeAttendance = Object.entries(sellerCounts).map(([sellerId, data]) => ({
            id: sellerId,
            user_id: sellerId,
            clock_in: data.lastInteraction,
            status: 'active',
            user: {
              id: sellerId,
              name: data.user.name,
              email: data.user.email,
              role: 'operador'
            }
          }));
          
          setActiveAttendance(activeAttendance);
        }
      } catch (err) {
        console.error('Erro ao carregar atendentes:', err);
      }

      // Carregar todos os atendentes disponíveis (para o modal de atribuição)
      try {
        const resUsers = await fetch(`${baseUrl}/api/users-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const dataUsers = await resUsers.json();
        if (dataUsers.success && dataUsers.users) {
          // Filtrar apenas operadores, admins e trainee
          const attendants = dataUsers.users.filter((u: any) => 
            u.role === 'operador' || u.role === 'admin' || u.role === 'trainee'
          );
          setAllAttendants(attendants);
        }
      } catch (err) {
        console.error('Erro ao carregar lista de atendentes:', err);
      }

      // Carregar chats aguardando (usuários com mensagens recentes)
      try {
        const resWaiting = await fetch(`${baseUrl}/api/chats/waiting`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const dataWaiting = await resWaiting.json();
        if (dataWaiting.success && dataWaiting.data) {
          setWaitingChats(dataWaiting.data);
        } else {
          // Fallback: buscar todos os usuários clientes
          const resStats = await fetch(`${baseUrl}/api/stats`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const dataStats = await resStats.json();
          if (dataStats.success) {
            // Simular chats aguardando (primeiros 10 usuários)
            setWaitingChats([]);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar chats aguardando:', err);
      }

      // Carregar estatísticas
      try {
        const resStats = await fetch(`${baseUrl}/api/chats/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const dataStats = await resStats.json();
        if (dataStats.success) {
          setStats(dataStats.data);
        } else {
          // Fallback: estatísticas básicas
          setStats({
            total: 0,
            active: 0,
            closed: 0,
            redistributed: 0
          });
        }
      } catch (err) {
        console.error('Erro ao carregar estatísticas:', err);
        setStats({
          total: 0,
          active: 0,
          closed: 0,
          redistributed: 0
        });
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAttendance = async (userId: string) => {
    if (!currentUser?.id) {
      alert('Erro: Usuário não identificado');
      return;
    }
    
    try {
      setAssigningChat(userId);
      const token = localStorage.getItem('token');
      
      // Atribuir seller_id ao usuário
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          seller_id: currentUser.id,
          atendimento: true,
          bot_enabled: false,
          in_service: true // Vai direto para "em atendimento"
        })
      });
      
      if (res.ok) {
        alert('Atendimento iniciado com sucesso!');
        loadData();
      } else {
        alert('Erro ao iniciar atendimento');
      }
    } catch (error) {
      console.error('Erro ao iniciar atendimento:', error);
      alert('Erro ao iniciar atendimento');
    } finally {
      setAssigningChat(null);
    }
  };

  const handleAssignToSeller = async (userId: string, sellerId: string) => {
    try {
      setAssigningChat(userId);
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          seller_id: sellerId,
          atendimento: true,
          bot_enabled: false,
          in_service: true // Vai direto para "em atendimento"
        })
      });
      
      if (res.ok) {
        alert('Chat atribuído com sucesso!');
        setShowAssignModal(null);
        loadData();
      } else {
        alert('Erro ao atribuir chat');
      }
    } catch (error) {
      console.error('Erro ao atribuir chat:', error);
      alert('Erro ao atribuir chat');
    } finally {
      setAssigningChat(null);
    }
  };

  const getWorkingTime = (clockIn: string) => {
    const start = new Date(clockIn);
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}min`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando painel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Painel de Atendimento</h1>
        <p className="text-gray-600">Monitore e gerencie a distribuição de chats</p>
      </div>

      {/* Cards de Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Atendentes Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{activeAttendance.length}</p>
              </div>
              <UserCheck className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Chats Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
              <BarChart3 className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Aguardando</p>
                <p className="text-2xl font-bold text-gray-900">{waitingChats.length}</p>
              </div>
              <Clock className="h-12 w-12 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Finalizados Hoje</p>
                <p className="text-2xl font-bold text-gray-900">{stats.closed}</p>
              </div>
              <Users className="h-12 w-12 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Atendentes Ativos */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Atendentes Ativos</h2>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {activeAttendance.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nenhum atendente ativo no momento
              </div>
            ) : (
              activeAttendance.map((attendance) => (
                <div key={attendance.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-green-600">
                          {attendance.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{attendance.user.name}</p>
                        <p className="text-sm text-gray-500">{attendance.user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-gray-900">
                          {getWorkingTime(attendance.clock_in)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {attendance.status === 'active' ? 'Disponível' : 'Em pausa'}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chats Aguardando */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">Chats Aguardando Atribuição</h2>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {waitingChats.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Nenhum chat aguardando atribuição
              </div>
            ) : (
              waitingChats.map((user) => (
                <div key={user.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-yellow-600">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.phone}</p>
                      </div>
                    </div>
                    <div>
                      {currentUser?.role === 'admin' ? (
                        showAssignModal === user.id ? (
                          <div className="flex flex-col gap-2 bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Atribuir para:</p>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {allAttendants.length === 0 ? (
                                <p className="text-xs text-gray-500 p-2">Nenhum atendente disponível</p>
                              ) : (
                                allAttendants.map((att) => (
                                  <button
                                    key={att.id}
                                    onClick={() => handleAssignToSeller(user.id, att.id)}
                                    disabled={assigningChat === user.id}
                                    className="w-full text-left text-xs px-3 py-2 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors disabled:opacity-50"
                                  >
                                    {att.username} ({att.role})
                                  </button>
                                ))
                              )}
                            </div>
                            <button
                              onClick={() => setShowAssignModal(null)}
                              className="text-xs py-1 px-3 bg-gray-200 hover:bg-gray-300 rounded text-gray-700"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowAssignModal(user.id)}
                            disabled={assigningChat === user.id}
                            className="text-xs py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            {assigningChat === user.id ? 'Atribuindo...' : 'Atribuir Atendimento'}
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => handleStartAttendance(user.id)}
                          disabled={assigningChat === user.id}
                          className="text-xs py-2 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                          {assigningChat === user.id ? 'Iniciando...' : (
                            <>
                              <UserCheck className="h-3 w-3" />
                              Iniciar Atendimento
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
